import { CianChatButtonWithAnimation } from '@cian/newbuilding-chat-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { onAnimationShown, onAnimationTooltipShown } from './analytics/analytics';
import {
  getChatAnimationCounter,
  getPrevPathCookie,
  getTooltipAnimationCookie,
  incrementChatAnimationCounter,
  setPrevPathCookie,
  setTooltipAnimationCookie,
} from './utils/chatAnimationCounter';
import { customLabelSelector, isChatExistSelector, isLoadingSelector } from '../../selectors/consultantCianChat';

export const CianChatButtonWithAnimationContainer = ({ onClick }: { onClick(): void }) => {
  const trackingCustomLabel = useSelector(customLabelSelector);
  const isLoading = useSelector(isLoadingSelector);
  const isChatExist = useSelector(isChatExistSelector);

  const [counter, setCounter] = React.useState(() => getChatAnimationCounter());
  const [isButtonAnimationVisible, setIsButtonAnimationVisible] = React.useState(() => counter % 5 === 0);

  const [isTooltipAnimationVisible, setIsTooltipAnimationVisible] = React.useState(() => {
    const tooltipCookieExists = getTooltipAnimationCookie();

    return !tooltipCookieExists && counter % 5 === 0 && !isChatExist;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    /* istanbul ignore next */
    if (typeof window === 'undefined') {
      return;
    }

    const currentPath = window.location.pathname + window.location.search;
    const prevPath = getPrevPathCookie();

    if (prevPath === null) {
      const newCounter = incrementChatAnimationCounter();

      setCounter(newCounter);
      setPrevPathCookie(currentPath);

      return;
    }

    if (prevPath !== currentPath) {
      const newCounter = incrementChatAnimationCounter();

      setCounter(newCounter);
      setIsButtonAnimationVisible(newCounter % 5 === 0);
      setPrevPathCookie(currentPath);
    }
  });

  React.useEffect(() => {
    const tooltipCookieExists = getTooltipAnimationCookie();
    if (tooltipCookieExists) {
      setIsTooltipAnimationVisible(false);
    } else {
      setIsTooltipAnimationVisible(isButtonAnimationVisible && !isChatExist);
    }
  }, [isButtonAnimationVisible, isChatExist]);

  /* istanbul ignore next */
  const handleAnimationStarted = React.useCallback(() => {
    onAnimationShown({ label: trackingCustomLabel });
    if (isTooltipAnimationVisible) {
      onAnimationTooltipShown({ label: trackingCustomLabel });
    }

    setTooltipAnimationCookie();
  }, [isTooltipAnimationVisible, trackingCustomLabel]);

  return (
    <CianChatButtonWithAnimation
      isLoading={isLoading}
      isButtonAnimationVisible={isButtonAnimationVisible}
      isTooltipAnimationVisible={isTooltipAnimationVisible}
      onClick={onClick}
      animationDelayMs={30000}
      onAnimationStarted={handleAnimationStarted}
    />
  );
};
